import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { useWorkScaleClass } from '@/design-system/work-scale'

/**
 * Dialog — общий модальный диалог (F2-04, design-tokens/component-catalog): на Radix
 * (`@radix-ui/react-dialog`), а не ad-hoc `<div role="dialog">` (как было в onboarding-modal.tsx
 * и training/training-request.tsx) — фокус-ловушка, закрытие по Escape и клику вне панели,
 * возврат фокуса триггеру и блокировка скролла страницы работают «из коробки» и одинаково
 * во всех местах использования.
 *
 * Корень бага F2-04: overlay-слой существовал, но красился классом `bg-black/60` — токена
 * `black` в палитре НЕТ (tailwind.config.js полностью заменяет цвета), поэтому класс не
 * генерировал CSS и скрим был прозрачным. Здесь — `bg-overlay` (`--overlay: rgba(0,0,0,.4)`,
 * канон `sout.html` `.drawer-overlay`) и `z-overlay` (`--z-overlay: 200`), заведомо выше
 * `z-sticky` (=10) шапки/сайдбара/поповеров, чтобы каркас не перекрывал диалог.
 *
 * Composition в стиле уже принятого в ДС паттерна Radix+токены (см. select.tsx):
 * `Dialog` (Root) / `DialogTrigger` / `DialogContent` / `DialogClose`.
 */
export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-overlay bg-overlay', className)}
    {...props}
  />
))
DialogOverlay.displayName = 'DialogOverlay'

export interface DialogContentProps extends ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /**
   * Программный заголовок для скринридеров (`DialogTitle`, визуально скрыт). Диалоги в этом
   * кабинете обычно уже показывают видимый заголовок внутри содержимого (например, `Card.header`) —
   * сюда достаточно продублировать тот же текст.
   */
  title: string
  /** Необязательное программное описание (`DialogDescription`, визуально скрыто). */
  description?: string
}

/**
 * Центрированная панель поверх `DialogOverlay`: полноэкранная flex-обёртка (не сам `Content` —
 * иначе клик по пустому полю скрима считался бы «внутри» диалога и не закрывал бы его) только
 * центрирует панель; скролл высокого содержимого (длинные формы/списки) — на самом `Content`
 * (`max-h-dialog overflow-y-auto`), НЕ на обёртке-предке (замечание РП №3, p2-remarks-2).
 *
 * Корень бага №3: Radix `Dialog` в модальном режиме оборачивает сам `Content` в
 * `react-remove-scroll` — это блокирует скролл страницы и ищет ближайший скроллящийся элемент
 * ВНУТРИ границы `Content` (а не у его предков) при обработке колеса мыши/тача. Пока overflow был
 * только на внешней обёртке-предке, у `Content` не было ни `max-h`, ни `overflow` — скроллящегося
 * элемента внутри границы не находилось, и `react-remove-scroll` просто блокировал скролл целиком
 * (модалка не прокручивалась к нижним полям/кнопкам). Перенос `max-h/overflow-y-auto` на сам
 * `Content` решает это: `Content` заведомо не выше 85vh (с запасом на `p-4` обёртки под 100vh),
 * поэтому в норме от обёртки скролл не требуется — `my-auto` центрирует панель как раньше.
 */
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, title, description, ...props }, ref) => {
  // Шкала шрифтов (centrika-docs!31): портал рендерится в `document.body`, вне DOM-поддерева
  // рабочей области, поэтому наследование до него не доходит и класс шкалы объявляется здесь —
  // но только если диалог открыт из кабинета. На страницах входа/регистрации контекста нет и
  // диалог остаётся на базовой шкале.
  const workScale = useWorkScaleClass()
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <div className={cn('fixed inset-0 z-overlay flex items-start justify-center p-4', workScale)}>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'my-auto max-h-dialog w-full max-w-narrow overflow-y-auto outline-none',
            className,
          )}
          {...props}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {description ? (
            <DialogPrimitive.Description className="sr-only">
              {description}
            </DialogPrimitive.Description>
          ) : null}
          {children}
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Portal>
  )
})
DialogContent.displayName = 'DialogContent'
